import React, { useEffect, type ReactNode } from "react";
import { useAuth } from "../hooks/useAuth";
import { LoadingScreen } from "./LoadingScreen";

export interface AuthGuardProps {
  children: ReactNode;
  /** Default "/login". Ignored if `onUnauthenticated` is provided. */
  redirectTo?: string;
  /**
   * Called instead of the default `window.location` redirect — e.g. to use
   * `react-router`'s `useNavigate()` for an in-app transition instead of a
   * full page reload. See `AuthRoutes` for a router-aware alternative.
   */
  onUnauthenticated?: () => void;
  /** Rendered while the initial session restore is still in flight. */
  fallback?: ReactNode;
}

/**
 * ```tsx
 * <AuthGuard>
 *   <ResourcePage />
 * </AuthGuard>
 * ```
 */
export function AuthGuard({ children, redirectTo = "/login", onUnauthenticated, fallback }: AuthGuardProps) {
  const { authenticated, loading } = useAuth();

  useEffect(() => {
    if (loading || authenticated) return;
    if (onUnauthenticated) {
      onUnauthenticated();
    } else if (typeof window !== "undefined") {
      window.location.assign(redirectTo);
    }
  }, [loading, authenticated, onUnauthenticated, redirectTo]);

  if (loading) return <>{fallback ?? <LoadingScreen />}</>;
  if (!authenticated) return null;
  return <>{children}</>;
}
